import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Users,
  AlertTriangle,
  CheckCircle2,
  Clock,
  TrendingUp,
  BookOpen,
  GraduationCap,
  CalendarDays,
  ArrowRight,
  FileText,
  Download,
  Loader2,
  Target,
  MessageSquare,
  Bell,
  Award,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role display names in Turkish
const roleNames = {
  tenant_owner: 'Kurum Sahibi',
  center_director: 'Merkez Müdürü',
  reading_coach: 'Okuma Koçu',
  teacher: 'Öğretmen',
  intervention_specialist: 'Müdahale Uzmanı',
  counselor: 'Rehber Öğretmen',
  parent: 'Veli',
  student: 'Öğrenci',
};

const StatCard = ({ icon: Icon, title, value, subtitle, trend, color = 'primary', loading = false }) => (
  <Card className="hover:shadow-md transition-shadow duration-200">
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin mt-2 text-muted-foreground" />
          ) : (
            <p className={`text-3xl font-bold mt-1 ${color === 'primary' ? 'text-primary-900' : `text-${color}`}`}>
              {value}
            </p>
          )}
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
          color === 'primary' ? 'bg-primary/10' : 
          color === 'red-500' ? 'bg-red-100' :
          color === 'green-500' ? 'bg-green-100' :
          color === 'yellow-500' ? 'bg-yellow-100' :
          'bg-primary/10'
        }`}>
          <Icon className={`h-5 w-5 ${
            color === 'primary' ? 'text-primary' :
            color === 'red-500' ? 'text-red-500' :
            color === 'green-500' ? 'text-green-500' :
            color === 'yellow-500' ? 'text-yellow-500' :
            'text-primary'
          }`} />
        </div>
      </div>
      {trend && (
        <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
          <TrendingUp className="h-3 w-3" />
          <span>{trend}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const TaskItem = ({ task }) => {
  const priorityColors = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-orange-100 text-orange-700',
    low: 'bg-green-100 text-green-700',
  };

  const typeIcons = {
    screening: Clock,
    intervention: Users,
    approval: FileText,
    report: FileText,
    message: MessageSquare,
  };

  const Icon = typeIcons[task.type] || FileText;

  return (
    <Link 
      to={task.link || '#'}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
    >
      <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center">
        <Icon className="h-4 w-4 text-slate-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{task.title}</p>
        <p className="text-xs text-muted-foreground">{task.due_date}</p>
      </div>
      <Badge variant="outline" className={priorityColors[task.priority]}>
        {task.priority === 'high' ? 'Acil' : task.priority === 'medium' ? 'Normal' : 'Düşük'}
      </Badge>
    </Link>
  );
};

const AlertItem = ({ alert }) => {
  const riskColors = {
    high: 'border-l-red-500 bg-red-50',
    medium: 'border-l-orange-500 bg-orange-50',
    low: 'border-l-green-500 bg-green-50',
  };

  return (
    <Link 
      to={`/students/${alert.student_id}`}
      className={`block p-3 rounded-r-lg border-l-4 ${riskColors[alert.risk]} hover:opacity-80 transition-opacity`}
    >
      <p className="text-sm font-medium text-slate-900">{alert.student_name}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{alert.message}</p>
    </Link>
  );
};

const KPICard = ({ kpi }) => {
  const statusColors = {
    good: 'text-green-600 bg-green-100',
    warning: 'text-yellow-600 bg-yellow-100',
    critical: 'text-red-600 bg-red-100',
  };

  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
      <div>
        <p className="text-sm text-muted-foreground">{kpi.title}</p>
        <p className="text-lg font-semibold">{kpi.value}</p>
      </div>
      <Badge className={statusColors[kpi.status]}>
        Hedef: {kpi.target}
      </Badge>
    </div>
  );
};

export const DashboardPage = () => {
  const { user, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [exporting, setExporting] = useState(false);

  const isAdmin = hasRole(['tenant_owner', 'center_director']);
  const isStaff = hasRole(['tenant_owner', 'center_director', 'reading_coach', 'teacher', 'intervention_specialist', 'counselor']);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const [statsRes, tasksRes, alertsRes, kpisRes] = await Promise.all([
          fetch(`${API_URL}/api/dashboard/stats`, { credentials: 'include' }),
          fetch(`${API_URL}/api/dashboard/tasks`, { credentials: 'include' }),
          fetch(`${API_URL}/api/dashboard/alerts`, { credentials: 'include' }),
          fetch(`${API_URL}/api/dashboard/kpis`, { credentials: 'include' }),
        ]);

        if (statsRes.ok) setStats(await statsRes.json());
        if (tasksRes.ok) setTasks(await tasksRes.json());
        if (alertsRes.ok) setAlerts(await alertsRes.json());
        if (kpisRes.ok) setKpis(await kpisRes.json());
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleExportStudents = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/export/students`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ogrenciler_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Öğrenci listesi indirildi');
      } else {
        toast.error('Export başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setExporting(false);
    }
  };

  const handleExportRiskReport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/export/risk-report`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `risk_raporu_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Risk raporu indirildi');
      } else {
        toast.error('Export başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setExporting(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Günaydın';
    if (hour < 18) return 'İyi günler';
    return 'İyi akşamlar';
  };

  const riskDistribution = stats?.risk_distribution || { high: 0, medium: 0, low: 0, none: 0 };
  const totalStudents = stats?.total_students || 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-900">
            {getGreeting()}, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground">
            {roleNames[user?.role]} • {user?.tenant?.name || 'BilgeOkur Merkezi'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isStaff && (
            <Button variant="outline" onClick={handleExportStudents} disabled={exporting}>
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              CSV İndir
            </Button>
          )}
          <Button asChild>
            <Link to="/students">
              <GraduationCap className="mr-2 h-4 w-4" />
              Öğrenciler
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={GraduationCap}
          title="Toplam Öğrenci"
          value={totalStudents}
          subtitle={`${stats?.total_classes || 0} sınıfta`}
          loading={loading}
        />
        <StatCard
          icon={AlertTriangle}
          title="Yüksek Risk"
          value={riskDistribution.high}
          subtitle="Acil müdahale gerekli"
          color="red-500"
          loading={loading}
        />
        <StatCard
          icon={Users}
          title="Aktif Müdahale"
          value={stats?.active_interventions || 0}
          subtitle="Bu dönem"
          loading={loading}
        />
        <StatCard
          icon={CheckCircle2}
          title="Tarama İlerlemesi"
          value={`${stats?.screening?.completed || 0}/${stats?.screening?.total || 0}`}
          subtitle="Dönem taraması"
          loading={loading}
        />
      </div>

      {/* KPIs for Admin */}
      {isAdmin && kpis.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Performans Göstergeleri</CardTitle>
            <Button variant="ghost" size="sm" onClick={handleExportRiskReport} disabled={exporting}>
              <Download className="mr-2 h-4 w-4" />
              Risk Raporu
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {kpis.map(kpi => (
                <KPICard key={kpi.id} kpi={kpi} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Risk distribution */}
      {isStaff && totalStudents > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Risk Dağılımı</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-red-700">Yüksek Risk</span>
                  <span className="text-sm text-muted-foreground">
                    {riskDistribution.high} öğrenci (%{Math.round(riskDistribution.high / totalStudents * 100)})
                  </span>
                </div>
                <Progress value={riskDistribution.high / totalStudents * 100} className="h-2 bg-slate-100" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-orange-700">Orta Risk</span>
                  <span className="text-sm text-muted-foreground">
                    {riskDistribution.medium} öğrenci (%{Math.round(riskDistribution.medium / totalStudents * 100)})
                  </span>
                </div>
                <Progress value={riskDistribution.medium / totalStudents * 100} className="h-2 bg-slate-100" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-green-700">Düşük Risk / Normal</span>
                  <span className="text-sm text-muted-foreground">
                    {riskDistribution.low + riskDistribution.none} öğrenci (%{Math.round((riskDistribution.low + riskDistribution.none) / totalStudents * 100)})
                  </span>
                </div>
                <Progress value={(riskDistribution.low + riskDistribution.none) / totalStudents * 100} className="h-2 bg-slate-100" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tasks and Alerts */}
      {isStaff && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tasks */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Bekleyen Görevler</CardTitle>
              {tasks.length > 0 && (
                <Badge variant="secondary">{tasks.length}</Badge>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : tasks.length > 0 ? (
                tasks.map(task => (
                  <TaskItem key={task.id} task={task} />
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Bekleyen görev yok
                </p>
              )}
            </CardContent>
          </Card>

          {/* Alerts */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Dikkat Gerektiren Öğrenciler</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/students?risk_level=high">
                  Tümü
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : alerts.length > 0 ? (
                alerts.map(alert => (
                  <AlertItem key={alert.id} alert={alert} />
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Dikkat gerektiren öğrenci yok
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick actions */}
      {isStaff && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Hızlı Erişim</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
                <Link to="/screenings">
                  <CalendarDays className="h-5 w-5 text-primary" />
                  <span className="text-xs">Taramalar</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
                <Link to="/students">
                  <GraduationCap className="h-5 w-5 text-primary" />
                  <span className="text-xs">Öğrenciler</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
                <Link to="/resources">
                  <BookOpen className="h-5 w-5 text-primary" />
                  <span className="text-xs">Kaynaklar</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
                <Link to="/cases">
                  <Award className="h-5 w-5 text-primary" />
                  <span className="text-xs">İyi Örnekler</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
                <Link to="/coaching">
                  <Target className="h-5 w-5 text-primary" />
                  <span className="text-xs">Koçluk</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" asChild>
                <Link to="/announcements">
                  <Bell className="h-5 w-5 text-primary" />
                  <span className="text-xs">Duyurular</span>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
